import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, filter, map, race, take, timer } from 'rxjs';
import { ProfileResolution, UserService } from '../services/user.service';

/**
 * Fail-open ceiling. If the profile never settles (a hung api/user/userProfile
 * that returns neither a response nor an error), do NOT block the whole app on
 * a blank screen waiting for it. After this long, allow navigation through to
 * /home. Bug 30363's catchError already suppresses the error toast, so the
 * worst case is an unresolved user landing on a permission-empty /home instead
 * of the dedicated page, which is strictly better than an infinite spinner.
 */
export const PROFILE_RESOLUTION_TIMEOUT_MS = 10_000;

/**
 * The routing decision for a settled profile resolution. Extracted as a pure
 * function so the branch that matters (unidentified -> terminal page) is unit
 * testable without the toObservable/timer plumbing.
 *
 * ONLY a definitive 'unidentified' (the server said there is no linked user)
 * diverts. 'identified' passes, and so do 'error' (a transport failure: the
 * user's link state is unknown, so a flaky API must not log them out to
 * /not-linked — PR 3803 P2 finding) and the never-reached 'pending', defensively.
 */
export function resolveUnidentifiedNavigation(
  resolution: ProfileResolution,
  router: Router
): boolean | UrlTree {
  return resolution === 'unidentified'
    ? router.createUrlTree(['/not-linked'])
    : true;
}

/**
 * Diverts an authenticated-but-unresolved user (#29713) to the terminal
 * /not-linked page instead of dropping them on a permission-empty /home with no
 * explanation (Bug 30363, scope-a UX).
 *
 * Runs AFTER issuerAwareAuthGuard on the shell route, so the caller is already
 * known to be authenticated. Here we only wait for UserService.profileResolution
 * to leave 'pending' (it is set asynchronously by UserEffects.load$ ->
 * fetchMyInfo once isAuthenticated$ emits) and act on the terminal state. The
 * /not-linked route itself does NOT carry this guard, so there is no redirect
 * loop.
 */
export const unidentifiedUserGuard: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  const userService = inject(UserService);
  const router = inject(Router);

  const settled$ = toObservable(userService.profileResolution).pipe(
    filter((resolution) => resolution !== 'pending'),
    take(1),
    map((resolution) => resolveUnidentifiedNavigation(resolution, router))
  );

  return race(
    settled$,
    timer(PROFILE_RESOLUTION_TIMEOUT_MS).pipe(
      map((): boolean | UrlTree => true)
    )
  );
};
